import buildQuery from "./query-builder";
import { accessLogService } from "./services/access-log.service";
import { arciveOrderService } from "./services/archive-order.service";
import { asnService } from "./services/asnService";
import { transferOrderService } from "./services/transferOrderService";
import { catalogService } from "./services/catalogService";
import { catalogTypeService } from "./services/catalogTypeService";
import { channelInventoryService } from "./services/channelInventoryService";
import { clientService } from "./services/clientService";
import { historicalShipmentService } from "./services/historical-shipments.service";
import { inventoryService } from "./services/inventoryService";
import { manifestService } from "./services/manifestService";
import { orderService } from "./services/orderService";
import { originZoneService } from "./services/originZoneService";
import { overpackService } from "./services/overpackService";
import { productService } from "./services/productService";
import { returnService } from "./services/returnService";
import { providerService } from "./services/providerService";
import { purchaseOrderService } from "./services/purchaseOrderService";
import { roleService } from "./services/role.service";
import { customerService } from "./services/customerService";
import { supplierService } from "./services/supplierService";
import { usersService } from "./services/usersService";
import { wmsService } from "./services/wmsProvider";
import { zoneChartService } from "./services/zoneChartService";
import { categoryService } from "./services/categoryService";
import { sidemenuService } from "./services/sidemenu.service";
import { distinctValuesService } from "./services/distinct-values.service";

export const orderFilterInitialState = {
  columns: [
    {
      key: "order_id",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchOrderId(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Order ID",
    },
    {
      key: "customer.name",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchCustomerName(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Customer Name",
    },
    {
      key: "shipping_address.province",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchCustomerState(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Customer State",
    },
    {
      key: "order_date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "shipments.shipment",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        orderService.fetchShipmentId(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Shipment ID",
    },
    {
      key: "sub_type",
      filter: [],
      remove_sort: true,
      options: [{ name: "B2B" }, { name: "B2C" }],
      sort: 0,
      id: "name",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
      placeholderKey: "Order Type",
    },
    {
      key: "status",
      filter: [],
      remove_sort: true,
      options: [
        { name: "Order Accepted" },
        { name: "Order Confirmed" },
        { name: "Label Created" },
        { name: "Fulfilled" },
        { name: "Shipped" },
        { name: "In Transit" },
        { name: "Out for Delivery" },
        { name: "On Hold" },
        { name: "Delivered" },
        { name: "Archived" },
        { name: "Cancelled" },
      ],
      sort: 0,
      id: "name",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "shipments.tracking_number",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        orderService.fetchTrackingNumber(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Tracking Number",
    },
    {
      key: "shipments.shipment_date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
      placeholderKey: "Shipment Date",
    },
    {
      key: "asn_no",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchPoOfOrders(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "PO No",
    },
    {
      key: "shipments.service",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        orderService.fetchService(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Shipping Service",
    },
    {
      key: "client_id",
      name: "email",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, null, searchKey, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      // Options come back already coalesced (`source_channel || oms_provider`)
      // so they match the column exactly; control-tower rewrites the resulting
      // `source_channel[$in]` query to match either field.
      key: "source_channel",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchOrderSource(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Order Source",
      excludeValues: ["All"],
    },
    {
      // TAGS column. `tags` is an array on the order, so a plain
      // `tags[$in][]=...` query already means "has any of these tags" in
      // Mongo — no server-side rewrite needed, unlike source_channel above.
      // Options come from control-tower's fetch-order-tags, which unwinds the
      // array so the list is distinct tags rather than distinct orders.
      key: "tags",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchOrderTags(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Tags",
      excludeValues: ["All"],
    },
  ],
  limit: 15,
  populate: [{ path: "client_id", select: ["business_name"] }],
  select: [
    "_id",
    "order_id",
    "type",
    "sub_type",
    "customer.name",
    "shipments.tracking_number",
    "shipments.service",
    "customer.address.province",
    "order_date",
    "shipments.shipment",
    "status",
    "error",
    "message",
    "asn_no",
    "shipping_address.province",
    "customer.first_name",
    "customer.last_name",
    "client_id",
    // ORDER SOURCE column. `oms_provider` is required too, not just nice to
    // have: it is the fallback the column renders for direct providers, which
    // never set source_channel. Anything omitted from this list is stripped by
    // control-tower's $select and the cell renders blank.
    "source_channel",
    "source_channel_type",
    "oms_provider",
    // Pipe17 order exceptions. Without these two in the projection the list
    // cannot show that an order is flagged at all — control-tower's $select
    // strips anything omitted here.
    "exceptions.type",
    "exceptions.status",
    // TAGS column.
    "tags",
  ],
};

export const productFilterInitialState = {
  columns: [
    {
      key: "product_type",
      name: "Product Type",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        productService.fetchCategory(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "category_id",
      name: "Category",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        categoryService.fetch(
          searchKey,
          client_id,
          ["name"],
          `$limit=${limit}&$skip=${skip}`,
        ),
    },
    {
      key: "client_id",
      name: "email",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, null, searchKey, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "status",
      name: "Status",
      filter: ["active"],
      options: [
        { name: "active" },
        { name: "inactive" },
        { name: "draft" },
        { name: "archived" },
      ],
      sort: 0,
      id: "name",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "vendor",
      name: "Supplier",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetch(
          searchKey,
          client_id,
          buildQuery({ limit, skip }),
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "no_of_sku",
      name: "No Of SKU",
      filter: [],
      options: Array.from({ length: 100 }).map((x, i) => ({
        name: (i + 1).toString(),
      })),
      sort: 0,
      id: "name",
      displayKey: "name",
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "compliance_status",
      name: "Compliance Status",
      filter: [],
      options: [
        { name: "Not Started" },
        { name: "In Progress" },
        { name: "Compliant" },
      ],
      sort: 0,
      id: "name",
      displayKey: "name",
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "_id",
      sort_key: "name",
      name: "Name",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        productService.fetchNames(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      excludeValues: ["All"],
    },
  ],
  limit: 15,
  populate: [
    { path: "client_id", select: ["business_name"] },
    { path: "vendor", select: ["name"] },
    { path: "category_id", select: ["name"] },
  ],
  select: [
    "_id",
    "name",
    "product_type",
    "vendor",
    "client_id",
    "status",
    "no_of_sku",
    "compliance_status",
    "images",
    // "variants",
    "category_id",
  ],
};

export const zoneChartOriginFilterInitialState = {
  columns: [
    {
      key: "origin",
      name: "Origin",
      sort: 0,
      id: "zip_3digit",
      filter: [],
      displayKey: "zip_3digit",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        originZoneService.fetch(limit, skip, searchKey),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "zone",
      name: "Zone",
      sort: 0,
      id: "zone",
      filter: [],
      displayKey: "zone",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        zoneChartService.fetchZoneList(limit, skip, searchKey),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "zipcodes",
      name: "Zipcode",
      sort: 0,
      id: "zipcode",
      filter: [],
      displayKey: "zipcode",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        zoneChartService.fetchZipcodeList(limit, skip, searchKey),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
  populate: [],
};

export const inventoryFilterInitialState = {
  columns: [
    {
      key: "sku",
      name: "SKU",
      sort: 0,
      id: "sku",
      filter: [],
      displayKey: "sku",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        distinctValuesService.fetchColumnValues(
          "inventory",
          "sku",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "product",
      name: "Product",
      sort: 0,
      id: "product",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        distinctValuesService.fetchColumnValues(
          "inventory",
          "product",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
      excludeValues: ["All"],
    },
    {
      key: "client",
      name: "Client",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "in_hand",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "in_transit",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "reorder_point",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "last_forecast_date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "last_po_created_date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "next_reorder_date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "warehouse",
      name: "Warehouse",
      sort: 0,
      id: "_id",
      displayKey: "warehouse_code",
      filter: [],
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        wmsService.fetch(client_id, limit, skip, null, searchKey),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "warehouse",
      name: "Warehouse Code",
      sort: 0,
      id: "_id",
      displayKey: "warehouse_code",
      filter: [],
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        wmsService.fetch(client_id, limit, skip),
    },
  ],
  limit: 15,
  populate: [
    { path: "product", select: ["name", "category", "product_type"] },
    { path: "provider", select: ["name"] },
    { path: "warehouse", select: ["warehouse_id", "warehouse_code"] },
    { path: "client", select: ["business_name"] },
  ],
};

export const historicalInventoryFilterInitialState = {
  columns: [
    {
      key: "sku",
      name: "SKU",
      sort: 0,
      id: "sku",
      filter: [],
      displayKey: "sku",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        distinctValuesService.fetchColumnValues(
          "historical-inventory",
          "sku",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "product",
      name: "Product",
      sort: 0,
      id: "product",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        distinctValuesService.fetchColumnValues(
          "historical-inventory",
          "product",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
      excludeValues: ["All"],
    },
    {
      key: "client",
      name: "Client",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "in_hand",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "in_transit",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "warehouse",
      name: "Warehouse Code",
      sort: 0,
      id: "_id",
      displayKey: "warehouse_code",
      filter: [],
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        wmsService.fetch(client_id, limit, skip),
    },
  ],
  limit: 15,
  populate: [
    { path: "product", select: ["name", "category", "product_type"] },
    { path: "warehouse", select: ["warehouse_code", "name"] },
    { path: "client", select: ["business_name"] },
  ],
};

export const shipmentFilterInitialState = {
  columns: [
    {
      key: "order_id",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchOrderId(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "shipment",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchShipmentId(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "response.shipment.labels.tracking_number",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        orderService.fetchTrackingNumber(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
  // populate: ["order"],
};

export const overpackFilterInitialState = {
  columns: [
    {
      name: "Overpack",
      key: "overpack_id",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        overpackService.fetchOverpackIds(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      name: "Exit Point",
      key: "overpack.exit_point",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        overpackService.fetchExitPoint(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      name: "Entry Point",
      key: "overpack.entry_point",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        overpackService.fetchEntryPoint(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      name: "Carrier",
      key: "overpack.carrier",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        overpackService.fetchCarrier(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
};

export const manifestFilterInitialState = {
  columns: [
    {
      name: "Entry Point",
      key: "manifest.entry_point",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        manifestService.fetchEntryPoint(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      name: "Carrier",
      key: "manifest.carrier",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        manifestService.fetchCarrier(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
  //  populate: ["client_id"],
};

export const historicalOrderFilterInitialState = {
  columns: [
    {
      key: "order_id",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        arciveOrderService.fetchUniqueValues(
          client_id,
          "order_id",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "order_date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "shipping_zip",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        arciveOrderService.fetchUniqueValues(
          client_id,
          "shipping_zip",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "shipping_city",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        arciveOrderService.fetchUniqueValues(
          client_id,
          "shipping_city",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "shipping_province",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        arciveOrderService.fetchUniqueValues(
          client_id,
          "shipping_province",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
  populate: ["client_id"],
};

export const hoistoricalShipmentFilterInitialState = {
  columns: [
    {
      key: "shipment_id",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        historicalShipmentService.fetchUniqueValues(
          client_id,
          "shipment_id",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "shipping_zip",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        historicalShipmentService.fetchUniqueValues(
          client_id,
          "shipping_zip",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "shipping_city",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        historicalShipmentService.fetchUniqueValues(
          client_id,
          "shipping_city",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "shipping_province",
      sort: 0,
      id: "key",
      filter: [],
      displayKey: "key",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        historicalShipmentService.fetchUniqueValues(
          client_id,
          "shipping_province",
          searchKey,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    // {
    //   key: "scale_weight",
    //   sort: 0,
    //   id: "key",
    //   filter: [],
    //   displayKey: "key",
    //   fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
    //     historicalShipmentService.fetchUniqueValues(
    //       client_id,
    //       "scale_weight",
    //       searchKey,
    //       limit,
    //       skip
    //     ),
    //   loadMoreButton: true,
    //   searchKey: "",
    // },
  ],
  limit: 15,
  populate: ["client_id"],
};

export const masterPOFilterInitialState = {
  columns: [
    {
      name: "Master PO",
      key: "no",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        purchaseOrderService.fetchMasterPoNo(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      name: "Client",
      key: "client.business_name",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "business_name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "supplier",
      name: "Supplier",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetch(
          searchKey,
          client_id,
          buildQuery({ limit, skip }),
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "amount",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "expiration_date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
  ],
  limit: 15,
  populate: ["client", "supplier"],
};

export const POFilterInitialState = {
  columns: [
    {
      name: "PO No",
      key: "asn_no",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        asnService.fetchPoNo(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      name: "Sku",
      key: "sku_list.sku",
      sort: 0,
      id: "finalSku",
      filter: [],
      displayKey: "finalSku",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        productService.fetchSKU(client_id, searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      excludeValues: ["All"],
    },
    {
      key: "order_id",
      name: "Order",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        asnService.fetchPoOrderId(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "supplier",
      name: "Supplier",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetch(
          searchKey,
          client_id,
          buildQuery({ limit, skip }),
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "client",
      name: "Client",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      // When the PO was raised at its source, not when we imported it — a
      // Pipe17 backfill can bring in POs raised long before this row existed,
      // so this is deliberately not `createdAt`. Sorted newest-first by
      // default, which is how operators read a PO list.
      key: "po_date",
      name: "PO Date",
      sort: -1,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "status",
      filter: [],
      remove_sort: true,
      options: [
        { name: "Pending" },
        { name: "Created" },
        { name: "On Hold" },
        { name: "Supplier Notified" },
        { name: "Supplier Confirmed" },
        { name: "Partially Fulfilled" },
        { name: "Inventory Ready" },
        { name: "Pickup Scheduled" },
        { name: "Picked Up" },
        { name: "Reached Warehouse" },
        { name: "Partially Received" },
        { name: "Stock-in Complete" },
        { name: "Returned to Supplier" },
        { name: "Cancelled" },
      ],
      sort: 0,
      id: "name",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
  ],
  limit: 15,
  populate: [
    "client",
    "supplier",
    "purchase_order",
    "sku_list.warehouse",
    { path: "sku_list.product", select: ["name", "variants"] },
  ],
};

export const notificationInitialState = {
  columns: [
    {
      name: "Type",
      key: "type",
      filter: [],
      remove_sort: true,
      options: [
        { name: "Supplier Config" },
        { name: "Purchase Order" },
        { name: "Inbound Delay" },
        { name: "Inventory" },
        { name: "Order Process" },
        { name: "Order Fulfillment" },
        { name: "Order Delivery" },
        { name: "Shipment" },
        { name: "Data Error" },
      ],
      sort: 0,
      id: "name",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      name: "triggered At",
      key: "createdAt",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "client_id",
      name: "Client",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
};

export const supplierFilterInitialState = {
  columns: [
    {
      key: "name",
      name: "Name",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetchName(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "email",
      name: "Email",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetchEmail(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "address.city",
      name: "City",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetchCity(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "address.province",
      name: "Province",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetchProvince(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "address.country",
      name: "Country",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        supplierService.fetchCountry(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
  populate: [],
};

export const customerFilterInitialState = {
  columns: [
    {
      key: "name",
      name: "Name",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        customerService.fetchUniqueField(
          "name",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "email",
      name: "Email",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        customerService.fetchUniqueField(
          "email",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "phone",
      name: "Phone",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        customerService.fetchUniqueField(
          "phone",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
  populate: [],
};

export const userFilterInitialState = {
  columns: [
    {
      key: "name",
      name: "Name",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        usersService.fetchUsersuniqueFields(
          "name",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "email",
      name: "Email",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        usersService.fetchUsersuniqueFields(
          "email",
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "roles",
      name: "Roles",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        roleService.fetch(limit, skip, searchKey),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "active",
      filter: [],
      remove_sort: true,
      options: [
        { name: "Active", value: true },
        { name: "Inactive", value: false },
      ],
      sort: 0,
      id: "value",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "clients",
      name: "Clients",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
  limit: 15,
  populate: [],
};

export const clientFilterInitialState = {
  columns: [
    {
      key: "name",
      name: "Name",
      filter: [],
      sort: 0,
      id: "name",
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, ["name"]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "business_name",
      name: "Business name",
      filter: [],
      sort: 0,
      id: "business_name",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "email",
      name: "email",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "email",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, null, searchKey, null, ["email"]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "status",
      filter: [],
      remove_sort: true,
      options: [
        { name: "Active" },
        { name: "Pending" },
        { name: "Inactive" },
        { name: "Rejected" },
        { name: "Approved" },
        { name: "Uninstalled" },
      ],
      sort: 0,
      id: "name",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "order_exclude_inventory",
      filter: [],
      remove_sort: true,
      options: [
        { name: "Active", value: true },
        { name: "Inactive", value: false },
      ],
      sort: 0,
      id: "value",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
  ],
  limit: 15,
  populate: [],
};

export const parameterFilterInitialState = {
  columns: [
    {
      key: "value",
      sort: 0,
      id: "value",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "date",
      sort: 0,
      id: "date",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "client_id",
      name: "Client",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
  ],
};

export const demandProfileFilterInitialState = {
  columns: [
    {
      key: "item",
      name: "Item",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "item",
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "period",
      name: "Period",
      filter: [],
      options: [
        { name: "Q1", value: 1 },
        { name: "Q2", value: 2 },
        { name: "Q3", value: 3 },
        { name: "Q4", value: 4 },
      ],
      sort: 0,
      id: "value",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
  ],
  limit: 15,
};

export const catalogFilterInitialState = {
  columns: [
    {
      key: "name",
      name: "Name",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        catalogService.fetchNames(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "type",
      name: "Type",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        catalogTypeService.fetch(limit, skip * limit, searchKey),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "client_id",
      name: "Client",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        clientService.fetch(limit, skip, null, searchKey, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "start_date",
      name: "Start Date",
      sort: 0,
      filter: { startDate: null, endDate: null, key: "selection" },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "end_date",
      name: "End Date",
      sort: 0,
      filter: { startDate: null, endDate: null, key: "selection" },
      loadMoreButton: false,
      type: "date-range",
    },
  ],
  limit: 15,
};

export const categoryFilterConfig = {
  columns: [
    {
      name: "Name",
      key: "_id",
      filter: [],
      sort: 0,
      sort_key: "_id",
      id: "_id",
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        categoryService.fetchNames(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      excludeValues: ["All"],
    },
    {
      key: "client",
      filter: [],
      sort: 0,
      sort_key: "client.business_name",
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetchBusinessName(searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
      excludeValues: ["All"],
    },
  ],
  limit: 15,
  populate: [{ path: "client", select: ["name", "business_name"] }],
};

// ─── Sales by Category filter config ─────────────────────────────────────────

export const salesByCategoryFilterConfig = {
  columns: [
    {
      key: "dimension",
      filter: [],
      sort: 0,
      sort_key: "dimension",
      id: "dimension",
      displayKey: "label",
      options: [
        { dimension: "category", label: "Category" },
        { dimension: "product_type", label: "Product Type" },
      ],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "category",
      filter: [],
      sort: 0,
      sort_key: "category.name",
      id: "_id",
      displayKey: "name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        categoryService.fetchNames(searchKey, client_id, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "client",
      filter: [],
      sort: 0,
      sort_key: "client.business_name",
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetchBusinessName(searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "month",
      filter: [],
      sort: 0,
      sort_key: "month",
      id: "month",
      displayKey: "label",
      options: [
        { month: 1, label: "January" },
        { month: 2, label: "February" },
        { month: 3, label: "March" },
        { month: 4, label: "April" },
        { month: 5, label: "May" },
        { month: 6, label: "June" },
        { month: 7, label: "July" },
        { month: 8, label: "August" },
        { month: 9, label: "September" },
        { month: 10, label: "October" },
        { month: 11, label: "November" },
        { month: 12, label: "December" },
      ],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "year",
      filter: [],
      sort: 0,
      sort_key: "year",
      id: "year",
      displayKey: "label",
      options: Array.from({ length: 20 }, (_, i) => {
        const y = new Date().getFullYear() - i;
        return { year: y, label: String(y) };
      }),
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "unit_sold",
      filter: {
        min: null,
        max: null,
      },
      sort: 0,
      id: "unit_sold",
      type: "number-range",
      loadMoreButton: false,
    },
    {
      key: "total_revenue",
      filter: {
        min: null,
        max: null,
      },
      sort: 0,
      id: "total_revenue",
      type: "number-range",
      loadMoreButton: false,
    },
    {
      key: "calculated_revenue",
      filter: {
        min: null,
        max: null,
      },
      sort: 0,
      id: "calculated_revenue",
      type: "number-range",
      loadMoreButton: false,
    },
    {
      key: "calculated_till",
      name: "Calculated Till",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      sort: 0,
      id: "_id",
      type: "date-range",
      loadMoreButton: false,
    },
  ],
  limit: 15,
  populate: [
    { path: "client", select: ["name", "business_name"] },
    { path: "category", select: ["name"] },
  ],
};

export const roleSidemenuFilterConfig = {
  columns: [
    {
      key: "sidemenu",
      name: "Menu Item",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "title",
      fetchApi: (searchKey, client_id, limit = 15, skip = 0) =>
        sidemenuService.fetch(
          `$limit=${limit}&$skip=${skip * limit}${searchKey ? `&$search=${searchKey}` : ""}`,
        ),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "system_generated",
      name: "System Generated",
      filter: [],
      sort: 0,
      id: "value",
      displayKey: "name",
      options: [
        { name: "Yes", value: "true" },
        { name: "No", value: "false" },
      ],
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "createdAt",
      name: "Created At",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      sort: 0,
      id: "_id",
      type: "date-range",
      loadMoreButton: false,
    },
  ],
  limit: 15,
  populate: [
    {
      path: "sidemenu",
      select: [
        "title",
        "order",
        "system_generated",
        "pkey",
        "parent",
        "icon",
        "active",
      ],
    },
    { path: "role", select: ["name"] },
  ],
};

export const sidemenuFilterConfig = {
  columns: [
    {
      key: "title",
      name: "Title",
      filter: [],
      sort: 0,
      id: "title",
      displayKey: "title",
      fetchApi: (searchKey, client_id, limit, skip) =>
        sidemenuService.fetchColumnValues("title", searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "order",
      name: "Order",
      filter: {
        min: null,
        max: null,
      },
      sort: 0,
      id: "_id",
      type: "number-range",
      loadMoreButton: false,
    },
    {
      key: "pkey",
      name: "Parent Menu",
      filter: [],
      sort: 0,
      id: "pkey",
      displayKey: "pkey",
      fetchApi: (searchKey, client_id, limit, skip) =>
        sidemenuService.fetchColumnValues("pkey", searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "parent",
      name: "Parent",
      filter: [],
      sort: 0,
      id: "parent",
      displayKey: "pkey",
      fetchApi: (searchKey, client_id, limit, skip) =>
        sidemenuService.fetchColumnValues("pkey", searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "system_generated",
      name: "System Generated",
      filter: [],
      sort: 0,
      id: "value",
      displayKey: "name",
      options: [
        { name: "Yes", value: "true" },
        { name: "No", value: "false" },
      ],
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "active",
      name: "Active",
      filter: [],
      sort: 0,
      id: "value",
      displayKey: "name",
      options: [
        { name: "Active", value: "true" },
        { name: "Inactive", value: "false" },
      ],
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "createdAt",
      name: "Created At",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      sort: 0,
      id: "_id",
      type: "date-range",
      loadMoreButton: false,
    },
  ],
  limit: 15,
  populate: [],
};

export const channelInventoryFilterInitialState = {
  columns: [
    {
      key: "sku",
      name: "SKU",
      sort: 0,
      id: "finalSku",
      filter: [],
      displayKey: "finalSku",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        productService.fetchSKU(client_id, searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "product",
      name: "Product",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        productService.fetch(
          client_id,
          searchKey,
          `$limit=${limit}&$skip=${skip * limit}&$select[]=name`,
          null,
        ),
      loadMoreButton: true,
      searchKey: "",
      type: "text-search",
      excludeValues: ["All"],
    },
    {
      // Filters on the upstream source, not the adapter. For Shopify/RL_OMS
      // rows source_channel is unset and the option falls back to provider, so
      // those keep filtering the way they always did.
      key: "source_channel",
      name: "Channel",
      sort: 0,
      id: "name",
      filter: [],
      displayKey: "name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        channelInventoryService.fetchSourceChannels(
          searchKey,
          client_id,
          limit,
          skip,
        ),
      loadMoreButton: false,
      searchKey: "",
      type: "text-search",
    },
    {
      key: "in_hand",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "in_transit",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "client_id",
      name: "Client",
      sort: 0,
      id: "_id",
      filter: [],
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "reorder_point",
      name: "Reorder Point",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
    {
      key: "last_po_created_date",
      name: "Last PO Created Date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "last_forecast_date",
      name: "Last Forecast Date",
      sort: 0,
      id: "_id",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      loadMoreButton: false,
      type: "date-range",
    },
    {
      key: "committed",
      name: "Committed",
      sort: 0,
      id: "_id",
      filter: {
        min: null,
        max: null,
      },
      loadMoreButton: false,
      type: "number-range",
    },
  ],
  populate: [
    { path: "product", select: ["name"] },
    { path: "client_id", select: ["business_name"] },
  ],
  limit: 15,
};

export const accessLogFilterInitialState = {
  columns: [
    {
      key: "email",
      name: "Email",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "_id",
      fetchApi: (searchKey, client_id, limit, skip) =>
        accessLogService.fetchUniqueValues("email", searchKey, limit, skip),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "client",
      name: "Client",
      filter: [],
      sort: 0,
      id: "_id",
      displayKey: "business_name",
      fetchApi: (searchKey, client_id, limit, skip) =>
        clientService.fetch(limit, skip, searchKey, null, null, [
          "business_name",
        ]),
      loadMoreButton: true,
      searchKey: "",
    },
    {
      key: "action",
      name: "Action",
      filter: [],
      remove_sort: true,
      options: [
        { name: "CREATE" },
        { name: "FIND" },
        { name: "DELETE" },
        { name: "UPDATE" },
      ],
      sort: 0,
      id: "name",
      displayKey: "name",
      excludeValues: ["all"],
      loadMoreButton: false,
      searchKey: "",
    },
    {
      key: "createdAt",
      name: "Timestamp",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      sort: 0,
      id: "_id",
      loadMoreButton: false,
      type: "date-range",
      placeholderKey: "Timestamp",
    },
  ],
  limit: 15,
  populate: [
    { path: "client", select: ["business_name"] },
    { path: "user", select: ["name"] },
  ],
};

/**
 * Returns list filters.
 *
 * Every dropdown is fed from `/distinct-values` on the `return` service rather
 * than a hardcoded option list, because the status vocabulary is the PROVIDER's
 * (Pipe17 has twelve states, Shopify five) and a hardcoded list would silently
 * hide any row whose status is not on it — the exact failure the "All" sentinel
 * in query-builder exists to avoid. Asking the data what values exist keeps the
 * dropdown correct as providers are added.
 */
export const returnFilterInitialState = {
  columns: [
    {
      // The return id. Needs a fetchApi like every other text column: with no
      // options to tick, FilterModal renders a search box whose value is only
      // ever passed back as `searchKey`, and buildQuery reads `filter`, not
      // `searchKey` — so the filter silently did nothing.
      name: "Return",
      key: "name",
      filter: [],
      sort: 0,
      id: "name",
      displayKey: "name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        returnService.fetchNames(searchKey, client_id, limit, skip),
      excludeValues: ["All"],
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Return",
    },
    {
      name: "Status",
      key: "status",
      filter: [],
      sort: 0,
      id: "status",
      displayKey: "status",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        returnService.fetchStatuses(searchKey, client_id, limit, skip),
      excludeValues: ["All"],
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Status",
    },
    {
      name: "Order",
      key: "order_name",
      filter: [],
      sort: 0,
      id: "order_name",
      displayKey: "order_name",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        returnService.fetchOrderNames(searchKey, client_id, limit, skip),
      excludeValues: ["All"],
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Order",
    },
    {
      name: "Source",
      key: "oms_provider",
      filter: [],
      sort: 0,
      id: "oms_provider",
      displayKey: "oms_provider",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        returnService.fetchProviders(searchKey, client_id, limit, skip),
      excludeValues: ["All"],
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Source",
    },
    {
      // The sales channel, stored on the return itself — so this filter is an
      // ordinary `channel[$in][]=` match, not a join. A return whose order we
      // do not hold has no stored channel and renders as Unknown; it cannot be
      // selected here, because there is no value to select.
      name: "Channel",
      key: "channel",
      filter: [],
      sort: 0,
      id: "channel",
      displayKey: "channel",
      fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
        returnService.fetchChannels(searchKey, client_id, limit, skip),
      excludeValues: ["All"],
      loadMoreButton: true,
      searchKey: "",
      placeholderKey: "Channel",
    },
    {
      // Filtered on `requested_at` (when the return was opened upstream) rather
      // than `createdAt` (when WE ingested it) — a backfill imports years of
      // history in one afternoon, so createdAt would put every historical return
      // in the same day and make the range filter useless.
      key: "requested_at",
      name: "Requested",
      filter: {
        startDate: null,
        endDate: null,
        key: "selection",
      },
      sort: 0,
      id: "_id",
      loadMoreButton: false,
      type: "date-range",
      placeholderKey: "Requested",
    },
  ],
  limit: 15,
};

// Transfer Orders list (Pipe17 transfers pulled into control-tower). Status
// values are Pipe17's own, so the dropdown offers exactly what Pipe17 reports.
const transferOrderDistinctColumn = (name, key, placeholderKey) => ({
  name,
  key,
  filter: [],
  sort: 0,
  id: key,
  displayKey: key,
  fetchApi: (searchKey = "", client_id, limit = 15, skip = 0) =>
    transferOrderService.fetchDistinct(key, searchKey, client_id, limit, skip),
  excludeValues: ["All"],
  loadMoreButton: true,
  searchKey: "",
  placeholderKey,
});

export const transferOrderFilterInitialState = {
  columns: [
    transferOrderDistinctColumn("Transfer", "external_id", "Transfer"),
    transferOrderDistinctColumn("Status", "status", "Status"),
    transferOrderDistinctColumn("From", "from_location_name", "From location"),
    transferOrderDistinctColumn("To", "to_location_name", "To location"),
    {
      name: "Expected Arrival",
      key: "expected_arrival_date",
      sort: 0,
      id: "_id",
      filter: { startDate: null, endDate: null, key: "selection" },
      loadMoreButton: false,
      type: "date-range",
    },
  ],
  limit: 15,
};
